import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";

type UseFittedFontSizeOptions = {
  /** Texto usado como referência para o “fit” (normalmente o valor final formatado). */
  text: string;
  /** Tamanho mínimo (px) aceito para caber no container. */
  minFontPx?: number;
  /** Tamanho máximo (px) desejado quando houver espaço. */
  maxFontPx?: number;
  /**
   * “Respiro” interno (px) a ser preservado dentro do container.
   * É subtraído do `clientWidth` antes do cálculo.
   */
  breathingRoomPx?: number;
  /** Desliga o auto-fit (mantém maxFontPx). */
  enabled?: boolean;
};

type UseFittedFontSizeResult<T extends HTMLElement> = {
  containerRef: MutableRefObject<T | null>;
  fontSizePx: number;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Calcula e mantém um `fontSize` (px) que garanta que `text` caiba na largura do container.
 *
 * Implementação:
 * - observa o container com `ResizeObserver`
 * - mede a largura do texto em um elemento invisível
 * - usa busca binária entre `minFontPx` e `maxFontPx`
 *
 * SRP: este hook só decide o tamanho de fonte; não formata texto e não anima valores.
 */
export function useFittedFontSize<T extends HTMLElement = HTMLDivElement>(
  options: UseFittedFontSizeOptions,
): UseFittedFontSizeResult<T> {
  const {
    text,
    minFontPx = 12,
    maxFontPx = 34,
    breathingRoomPx = 20,
    enabled = true,
  } = options;

  const containerRef = useRef<T>(null);
  const measureElRef = useRef<HTMLSpanElement | null>(null);
  const [fontSizePx, setFontSizePx] = useState<number>(maxFontPx);

  // Cria um elemento invisível de medição por instância.
  useLayoutEffect(() => {
    if (!enabled) return;
    const el = document.createElement("span");
    el.setAttribute("data-fit-text-measure", "true");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    el.style.top = "-9999px";
    el.style.visibility = "hidden";
    el.style.pointerEvents = "none";
    el.style.whiteSpace = "nowrap";
    document.body.appendChild(el);
    measureElRef.current = el;
    return () => {
      el.remove();
      measureElRef.current = null;
    };
  }, [enabled]);

  const recompute = useCallback(() => {
    if (!enabled) {
      setFontSizePx(maxFontPx);
      return;
    }

    const container = containerRef.current;
    const measureEl = measureElRef.current;
    if (!container || !measureEl) return;

    const availableWidth = Math.floor(container.clientWidth - breathingRoomPx);
    if (!Number.isFinite(availableWidth) || availableWidth <= 0) return;

    const safeText = String(text ?? "");
    measureEl.textContent = safeText;

    // Copiar propriedades que afetam a largura do texto.
    const cs = window.getComputedStyle(container);
    measureEl.style.fontFamily = cs.fontFamily;
    measureEl.style.fontWeight = cs.fontWeight;
    measureEl.style.fontStyle = cs.fontStyle;
    measureEl.style.letterSpacing = cs.letterSpacing;
    measureEl.style.textTransform = cs.textTransform;
    // Importante para números (tabular-nums) e font-feature-settings
    const csExtras = cs as CSSStyleDeclaration & {
      fontVariantNumeric?: string;
      fontFeatureSettings?: string;
    };
    measureEl.style.fontVariantNumeric = csExtras.fontVariantNumeric ?? "";
    measureEl.style.fontFeatureSettings = csExtras.fontFeatureSettings ?? "";

    // Busca binária pelo maior font-size que cabe.
    let lo = clamp(Math.floor(minFontPx), 6, 200);
    let hi = clamp(Math.floor(maxFontPx), lo, 220);
    let best = lo;

    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      measureEl.style.fontSize = `${mid}px`;

      const textWidth = Math.ceil(measureEl.getBoundingClientRect().width);
      if (textWidth <= availableWidth) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    setFontSizePx(best);
  }, [breathingRoomPx, enabled, maxFontPx, minFontPx, text]);

  // Recalcular quando o texto muda (ex.: mudança de período/filtro).
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    recompute();
  }, [recompute]);

  // Recalcular quando o container muda de tamanho.
  useLayoutEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;
    if (!window.ResizeObserver) {
      const onResize = () => recompute();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    const ro = new ResizeObserver(() => recompute());
    ro.observe(container);
    return () => ro.disconnect();
  }, [enabled, recompute]);

  return { containerRef, fontSizePx };
}
